import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    CaretLeft,
    CircleNotch,
    CurrencyDollar,
    Info,
    Sparkle,
    WarningCircle,
} from '@phosphor-icons/react';
import type { ModuleTypeOptionData } from '../../@types/types';
import moduleServices from '../../services/moduleService';
import {
    getPreContractModuleShadeColor,
    getPreContractShadeColor,
    isPreContractPanelAllowed,
    isPreContractPurplePanel,
    isPreContractShadePending,
} from '../lib/preContractModuleLayout';
import {
    averageIncludedMultiplier,
    roofIncludedMultiplierDetail,
} from '../lib/preContractPpwPricing';
import type {
    MaxfillTargetCardProps,
    PreContractLayoutTab,
    PreContractModuleStepProps,
    RoofModuleBoardProps,
    SiteTreeBoardProps,
    SiteTreeIconProps,
} from './PreContractModuleStep.types';
import type { PreContractFillOrientation } from '../lib/preContractTypes';
import { FILL_ORIENTATION_OPTIONS, parseFillOrientations } from '../lib/preContractTypes';

const COARSE_SA_LEGEND = [
    { label: '<30% SA', sample: 25 },
    { label: '30–50% SA', sample: 40 },
    { label: '50–74% SA', sample: 60 },
    { label: '75–94% SA', sample: 80 },
    { label: '95–100% SA', sample: 96 },
] as const;

const SA_LEGEND_POPOVER_WIDTH = 280;
const SA_LEGEND_POPOVER_GAP = 10;
const SA_LEGEND_VIEWPORT_MARGIN = 8;

export const PreContractModuleStep: React.FC<PreContractModuleStepProps> = ({
    form,
    patchForm,
    onBack,
    onRunAiDesign,
    onOpenPricing,
    actionsDisabled = false,
    runAiDisabled,
    hasPreviousAiDesigns = false,
    viewportSlot,
    renderViewport = true,
    embedded = false,
    onTreeCuttingActiveChange,
}) => {
    const [moduleOptions, setModuleOptions] = useState<ModuleTypeOptionData[]>([]);
    const [moduleLoadError, setModuleLoadError] = useState<string | null>(null);
    const [hoverCell, setHoverCell] = useState<{
        roofIndex: number;
        moduleIndex: number;
    } | null>(null);
    const [layoutTab, setLayoutTab] = useState<PreContractLayoutTab>('modules');

    useEffect(() => {
        onTreeCuttingActiveChange?.(layoutTab === 'trees');
        return () => onTreeCuttingActiveChange?.(false);
    }, [layoutTab, onTreeCuttingActiveChange]);

    const allRoofBoards = form.roofModuleLayouts;

    const shadeLoad = useMemo(() => {
        let present = 0;
        let pending = 0;
        form.roofModuleLayouts.forEach((layout) => {
            layout.modules.forEach((cell) => {
                if (!cell) return;
                present += 1;
                if (isPreContractShadePending(cell.solarAccess)) pending += 1;
            });
        });
        return { present, pending };
    }, [form.roofModuleLayouts]);
    const modulesLoading = shadeLoad.present === 0 || shadeLoad.pending > 0;
    const anyRoofActive = useMemo(
        () => form.roofModuleLayouts.some((layout) => layoutIsActive(layout)),
        [form.roofModuleLayouts],
    );
    const systemMultiplier = useMemo(
        () => averageIncludedMultiplier(form.roofModuleLayouts, form.roofSelections),
        [form.roofModuleLayouts, form.roofSelections],
    );
    const saLegendTriggerRef = useRef<HTMLButtonElement>(null);
    const saLegendPopoverRef = useRef<HTMLDivElement>(null);
    const saLegendCloseTimerRef = useRef<number | null>(null);
    const [saLegendOpen, setSaLegendOpen] = useState(false);
    const [saLegendPos, setSaLegendPos] = useState<{
        top: number;
        left: number;
        placement: 'below' | 'above';
    } | null>(null);

    const clearSaLegendCloseTimer = useCallback(() => {
        if (saLegendCloseTimerRef.current != null) {
            window.clearTimeout(saLegendCloseTimerRef.current);
            saLegendCloseTimerRef.current = null;
        }
    }, []);

    const openSaLegend = useCallback(() => {
        clearSaLegendCloseTimer();
        setSaLegendOpen(true);
    }, [clearSaLegendCloseTimer]);

    const scheduleCloseSaLegend = useCallback(() => {
        clearSaLegendCloseTimer();
        saLegendCloseTimerRef.current = window.setTimeout(() => setSaLegendOpen(false), 120);
    }, [clearSaLegendCloseTimer]);

    const updateSaLegendPopoverPosition = useCallback(() => {
        const trigger = saLegendTriggerRef.current;
        if (!trigger) return;

        const triggerRect = trigger.getBoundingClientRect();
        const popoverWidth = saLegendPopoverRef.current?.offsetWidth ?? SA_LEGEND_POPOVER_WIDTH;
        const popoverHeight = saLegendPopoverRef.current?.offsetHeight ?? 220;

        let left = triggerRect.left + triggerRect.width / 2 - popoverWidth / 2;
        const maxLeft = window.innerWidth - popoverWidth - SA_LEGEND_VIEWPORT_MARGIN;
        left = Math.max(SA_LEGEND_VIEWPORT_MARGIN, Math.min(left, maxLeft));

        const spaceBelow =
            window.innerHeight - triggerRect.bottom - SA_LEGEND_POPOVER_GAP - SA_LEGEND_VIEWPORT_MARGIN;
        const spaceAbove = triggerRect.top - SA_LEGEND_POPOVER_GAP - SA_LEGEND_VIEWPORT_MARGIN;
        const placement: 'below' | 'above' =
            spaceBelow >= popoverHeight || spaceBelow >= spaceAbove ? 'below' : 'above';

        let top =
            placement === 'below'
                ? triggerRect.bottom + SA_LEGEND_POPOVER_GAP
                : triggerRect.top - popoverHeight - SA_LEGEND_POPOVER_GAP;

        const maxTop = window.innerHeight - popoverHeight - SA_LEGEND_VIEWPORT_MARGIN;
        top = Math.max(SA_LEGEND_VIEWPORT_MARGIN, Math.min(top, maxTop));

        setSaLegendPos({ top, left, placement });
    }, []);

    useLayoutEffect(() => {
        if (!saLegendOpen) {
            setSaLegendPos(null);
            return;
        }
        updateSaLegendPopoverPosition();
        const frame = requestAnimationFrame(() => updateSaLegendPopoverPosition());
        return () => cancelAnimationFrame(frame);
    }, [saLegendOpen, updateSaLegendPopoverPosition]);

    useEffect(() => {
        if (!saLegendOpen) return undefined;
        const onReflow = () => updateSaLegendPopoverPosition();
        window.addEventListener('resize', onReflow);
        window.addEventListener('scroll', onReflow, true);
        return () => {
            window.removeEventListener('resize', onReflow);
            window.removeEventListener('scroll', onReflow, true);
        };
    }, [saLegendOpen, updateSaLegendPopoverPosition]);

    useEffect(() => () => clearSaLegendCloseTimer(), [clearSaLegendCloseTimer]);

    const saLegendPopoverContent = (
        <div className="pci-sa-legend pci-sa-legend--popover" aria-label="Solar access legend">
            {COARSE_SA_LEGEND.map((band) => (
                <span key={band.label} className="pci-sa-legend__item">
                    <i style={{ background: getPreContractShadeColor(band.sample) }} />
                    {band.label}
                </span>
            ))}
            <span className="pci-sa-legend__item">
                <i className="pci-sa-legend__hatch" />
                Not used
            </span>
            <span className="pci-sa-legend__item">
                <i className="pci-sa-legend__loading" />
                Loading
            </span>
        </div>
    );

    const saLegendPopover =
        saLegendOpen
            ? createPortal(
                  <div
                      ref={saLegendPopoverRef}
                      className={`pci-utility-popover pci-utility-popover--portal pci-sa-legend-popover${
                          saLegendPos?.placement === 'above' ? ' pci-utility-popover--above' : ''
                      }`}
                      role="tooltip"
                      aria-label="Solar access legend"
                      style={{
                          top: saLegendPos?.top ?? -9999,
                          left: saLegendPos?.left ?? -9999,
                          visibility: saLegendPos ? 'visible' : 'hidden',
                      }}
                      onMouseEnter={openSaLegend}
                      onMouseLeave={scheduleCloseSaLegend}
                  >
                      {saLegendPopoverContent}
                  </div>,
                  document.body,
              )
            : null;

    const moduleSaLegend = (
        <div className="pci-sa-legend-footer">
            <span className="pci-sa-legend__item pci-sa-legend__item--system-mult">
                System mult{' '}
                <strong>{systemMultiplier != null ? systemMultiplier.toFixed(3) : '—'}</strong>
            </span>
            <button
                ref={saLegendTriggerRef}
                type="button"
                className="pci-sa-legend-info"
                aria-label="Solar access legend"
                aria-expanded={saLegendOpen}
                onMouseEnter={openSaLegend}
                onMouseLeave={scheduleCloseSaLegend}
                onFocus={openSaLegend}
                onBlur={scheduleCloseSaLegend}
            >
                <Info size={14} weight="fill" aria-hidden />
            </button>
            {saLegendPopover}
        </div>
    );
    const runAiBlocked = runAiDisabled ?? actionsDisabled;
    const runNewDisabled = runAiBlocked || modulesLoading || !anyRoofActive;
    const viewPreviousDisabled = actionsDisabled;

    const runAiTitle = useMemo(() => {
        if (modulesLoading) return 'Wait until all modules finish loading shade';
        if (!anyRoofActive) return 'Select at least one module to run AI designs';
        if (runAiBlocked) {
            return 'Complete required intake steps (products, site, utility, design) to run AI';
        }
        return hasPreviousAiDesigns ? 'Run new AI designs' : 'Run AI designs';
    }, [modulesLoading, anyRoofActive, runAiBlocked, hasPreviousAiDesigns]);

    const selectedFills = parseFillOrientations(form.fillOrientations);
    const toggleFillOrientation = (id: PreContractFillOrientation) => {
        const on = selectedFills.includes(id);
        patchForm({
            fillOrientations: on ? selectedFills.filter((item) => item !== id) : [...selectedFills, id],
        });
    };

    useEffect(() => {
        let cancelled = false;
        const stateCode = form.installState || 'CT';

        void (async () => {
            try {
                const res = await moduleServices.getModuleList(stateCode);
                const modules = (res?.Modules ?? []) as Array<{
                    Name: string;
                    Wattage: number;
                    WidthInMm: number;
                    LengthInMm: number;
                    Manufacturer: string;
                }>;
                if (cancelled) return;
                const options: ModuleTypeOptionData[] = modules.map((m, index) => ({
                    key: index,
                    text: m.Name,
                    value: m.Name,
                    power: m.Wattage,
                    w: m.WidthInMm,
                    h: m.LengthInMm,
                    manufacturer: m.Manufacturer,
                }));
                setModuleOptions(options);
                setModuleLoadError(null);
                if (!form.moduleTypeName && options.length > 0) {
                    const preferred =
                        options.find((o) => /410/.test(o.text)) ?? options[0];
                    patchForm({
                        moduleTypeName: preferred.text,
                        moduleTypeWattage: preferred.power,
                    });
                }
            } catch {
                if (!cancelled) {
                    setModuleLoadError('Could not load module types for this state.');
                    setModuleOptions([
                        {
                            key: 0,
                            text: 'Hanwha - Q.PEAK DUO BLK ML-G10 plus 410',
                            value: 'Hanwha - Q.PEAK DUO BLK ML-G10 plus 410',
                            power: 410,
                            w: 1045,
                            h: 1879,
                            manufacturer: 'Hanwha',
                        },
                        {
                            key: 1,
                            text: 'Hanwha - Q.PEAK DUO BLK ML-G10 plus 405',
                            value: 'Hanwha - Q.PEAK DUO BLK ML-G10 plus 405',
                            power: 405,
                            w: 1045,
                            h: 1879,
                            manufacturer: 'Hanwha',
                        },
                        {
                            key: 2,
                            text: 'Hanwha - Q.PEAK DUO BLK ML-G10+ 400',
                            value: 'Hanwha - Q.PEAK DUO BLK ML-G10+ 400',
                            power: 400,
                            w: 1045,
                            h: 1879,
                            manufacturer: 'Hanwha',
                        },
                    ]);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
        // Intentionally only re-fetch when state changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.installState]);

    const cellBlocked = (cell: { solarAccess: number } | null | undefined) =>
        Boolean(cell) &&
        !isPreContractPanelAllowed(
            cell!.solarAccess,
            form.includeGreyPanels,
            form.includePurplePanels,
        );

    /** Grey/purple panels cannot stay selected while their toggle is off. */
    useEffect(() => {
        let changed = false;
        const nextLayouts = form.roofModuleLayouts.map((layout) => ({
            ...layout,
            modules: layout.modules.map((cell) => {
                if (!cell?.included || !cellBlocked(cell)) return cell;
                changed = true;
                return { ...cell, included: false };
            }),
        }));
        if (!changed) return;
        patchForm({
            roofModuleLayouts: nextLayouts,
            roofSelections: selectionsFromLayouts(nextLayouts),
        });
    }, [form.includeGreyPanels, form.includePurplePanels, form.roofModuleLayouts]);

    const toggleModule = (roofIndex: number, moduleIndex: number) => {
        const target = form.roofModuleLayouts.find((layout) => layout.roofIndex === roofIndex)
            ?.modules[moduleIndex];
        if (!target) return;
        if (!target.included && cellBlocked(target)) return;
        const nextLayouts = form.roofModuleLayouts.map((layout) => {
            if (layout.roofIndex !== roofIndex) return layout;
            return {
                ...layout,
                modules: layout.modules.map((cell, idx) => {
                    if (idx !== moduleIndex || !cell) return cell;
                    return { ...cell, included: !cell.included };
                }),
            };
        });
        patchForm({
            roofModuleLayouts: nextLayouts,
            roofSelections: selectionsFromLayouts(nextLayouts),
        });
    };

    const setRoofModulesIncluded = (roofIndex: number, included: boolean) => {
        const nextLayouts = form.roofModuleLayouts.map((layout) => {
            if (layout.roofIndex !== roofIndex) return layout;
            return {
                ...layout,
                modules: layout.modules.map((cell) => {
                    if (!cell) return null;
                    if (included && cellBlocked(cell)) return { ...cell, included: false };
                    return { ...cell, included };
                }),
            };
        });
        patchForm({
            roofSelections: selectionsFromLayouts(nextLayouts),
            roofModuleLayouts: nextLayouts,
        });
    };

    const toggleTreeGhost = (treeId: string) => {
        const siteTrees = form.siteTrees.map((tree) =>
            tree.id === treeId ? { ...tree, ghosted: !tree.ghosted } : tree,
        );
        patchForm({ siteTrees, treeCount: siteTrees.length });
    };

    const showModulesTab = () => setLayoutTab('modules');
    const showTreesTab = () => setLayoutTab('trees');

    const firstModuleCell = useMemo(() => {
        for (const layout of form.roofModuleLayouts) {
            const moduleIndex = layout.modules.findIndex(
                (cell) => Boolean(cell && 'id' in cell),
            );
            if (moduleIndex >= 0) {
                return { roofIndex: layout.roofIndex, moduleIndex };
            }
        }
        return null;
    }, [form.roofModuleLayouts]);

    const activeModuleCell = useMemo(() => {
        if (hoverCell) {
            const layout = form.roofModuleLayouts.find(
                (l) => l.roofIndex === hoverCell.roofIndex,
            );
            const cell = layout?.modules[hoverCell.moduleIndex];
            if (cell && 'id' in cell) return hoverCell;
        }
        return firstModuleCell;
    }, [hoverCell, firstModuleCell, form.roofModuleLayouts]);

    const hoverLayout = activeModuleCell
        ? form.roofModuleLayouts.find((l) => l.roofIndex === activeModuleCell.roofIndex)
        : null;
    const hoverModule =
        hoverLayout && activeModuleCell
            ? hoverLayout.modules[activeModuleCell.moduleIndex]
            : null;
    const hoverModuleCell = hoverModule && 'id' in hoverModule ? hoverModule : null;

    const configBar = (
        <div
            className={`pci-config-bar pci-config-bar--inline${
                embedded ? ' pci-config-bar--compact-grid' : ''
            }`}
            role="group"
            aria-label="Design configuration"
        >
            <div className="pci-config-bar__group">
                <span className="pci-config-bar__label">Shaded panels</span>
                <div className="pci-choice-group" role="group" aria-label="Shaded panels">
                    <button
                        type="button"
                        className={`pci-choice pci-choice--compact pci-choice--grey${
                            form.includeGreyPanels ? ' pci-choice--on' : ''
                        }`}
                        aria-pressed={form.includeGreyPanels}
                        title="Include grey panels (30–50% solar access)"
                        onClick={() => patchForm({ includeGreyPanels: !form.includeGreyPanels })}
                    >
                        Grey
                    </button>
                    <button
                        type="button"
                        className={`pci-choice pci-choice--compact pci-choice--purple${
                            form.includePurplePanels ? ' pci-choice--on' : ''
                        }`}
                        aria-pressed={form.includePurplePanels}
                        title="Include purple panels (<30% solar access)"
                        onClick={() => patchForm({ includePurplePanels: !form.includePurplePanels })}
                    >
                        Purple
                    </button>
                </div>
            </div>
            <div className="pci-config-bar__group">
                <span className="pci-config-bar__label">Orientation</span>
                <div className="pci-choice-group" role="group" aria-label="Module orientation">
                    {FILL_ORIENTATION_OPTIONS.map((option) => (
                        <button
                            key={option.id}
                            type="button"
                            className={`pci-choice pci-choice--compact${
                                selectedFills.includes(option.id) ? ' pci-choice--on' : ''
                            }`}
                            title={option.hint + ' (optional — leave both off to allow portrait and landscape)'}
                            aria-pressed={selectedFills.includes(option.id)}
                            onClick={() => toggleFillOrientation(option.id)}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            </div>
            <div className="pci-config-bar__group">
                <span className="pci-config-bar__label">Priority</span>
                <div className="pci-choice-group" role="group" aria-label="Design priority">
                    <button
                        type="button"
                        className={`pci-choice pci-choice--compact${
                            form.offsetPreference === 'full' ? ' pci-choice--on' : ''
                        }`}
                        aria-pressed={form.offsetPreference === 'full'}
                        title="Optional — prioritize covering annual usage"
                        onClick={() =>
                            patchForm({
                                offsetPreference: form.offsetPreference === 'full' ? null : 'full',
                            })
                        }
                    >
                        Production
                    </button>
                    <button
                        type="button"
                        className={`pci-choice pci-choice--compact${
                            form.offsetPreference === 'balanced' ? ' pci-choice--on' : ''
                        }`}
                        aria-pressed={form.offsetPreference === 'balanced'}
                        title="Optional — prioritize clean, high-SA arrays"
                        onClick={() =>
                            patchForm({
                                offsetPreference:
                                    form.offsetPreference === 'balanced' ? null : 'balanced',
                            })
                        }
                    >
                        Aesthetics
                    </button>
                </div>
            </div>
        </div>
    );

    const layoutToolbarActions = embedded ? (
        <div className="pci-layout-toolbar__actions">
            <button
                type="button"
                className="pci-board-fab__ai-btn pci-board-fab__ai-btn--primary pci-board-fab__ai-btn--solo pci-board-fab__ai-btn--compact"
                disabled={runNewDisabled}
                title={runAiTitle}
                onClick={() => onRunAiDesign(false)}
            >
                <Sparkle size={16} weight="fill" />
                {modulesLoading ? 'Waiting…' : 'Run AI'}
            </button>
        </div>
    ) : null;

    return (
        <div
            id="pci-section-layout"
            className={`pci-module-step pci-module-step--v3${
                embedded ? ' pci-module-step--embedded' : ''
            }`}
        >
            {renderViewport ? viewportSlot : null}

            <section className="pci-module-step__card pci-module-step__card--layout">
                <div
                    className={`pci-layout-toolbar${
                        embedded ? ' pci-layout-toolbar--embedded' : ''
                    }`}
                >
                    <div className="pci-layout-toolbar__row pci-layout-toolbar__row--head">
                        <div className="pci-layout-tabs" role="tablist" aria-label="Layout views">
                            <button
                                type="button"
                                role="tab"
                                className={`pci-layout-tabs__btn${
                                    layoutTab === 'modules' ? ' pci-layout-tabs__btn--on' : ''
                                }`}
                                aria-selected={layoutTab === 'modules'}
                                onClick={showModulesTab}
                            >
                                Physical module layout
                            </button>
                            <button
                                type="button"
                                role="tab"
                                className={`pci-layout-tabs__btn${
                                    layoutTab === 'trees' ? ' pci-layout-tabs__btn--on' : ''
                                }`}
                                aria-selected={layoutTab === 'trees'}
                                onClick={showTreesTab}
                            >
                                Tree Cutting
                            </button>
                        </div>
                        {embedded ? layoutToolbarActions : null}
                    </div>
                    {layoutTab === 'modules' ? (
                        <div className="pci-layout-toolbar__row pci-layout-toolbar__row--config">
                            {configBar}
                        </div>
                    ) : null}
                    {!embedded ? layoutToolbarActions : null}
                </div>
                {layoutTab === 'modules' ? (
                    <>
                <div className="pci-module-layout-grid">
                    {allRoofBoards.length > 0 ? (
                        allRoofBoards.map((layout) => {
                            const roofActive = layoutIsActive(layout);
                            const multiplierDetail = roofActive
                                ? roofIncludedMultiplierDetail(layout)
                                : {
                                      multiplier: null,
                                      meanSolarAccess: null,
                                      includedCount: 0,
                                  };
                            return (
                                <RoofModuleBoard
                                    key={`${layout.roofIndex}-${layout.label}`}
                                    layout={layout}
                                    multiplierDetail={multiplierDetail}
                                    inactive={!roofActive}
                                    includeGreyPanels={form.includeGreyPanels}
                                    includePurplePanels={form.includePurplePanels}
                                    onToggleRoof={() =>
                                        setRoofModulesIncluded(layout.roofIndex, !roofActive)
                                    }
                                    onToggle={(moduleIndex) =>
                                        toggleModule(layout.roofIndex, moduleIndex)
                                    }
                                    onHover={(moduleIndex) =>
                                        setHoverCell({
                                            roofIndex: layout.roofIndex,
                                            moduleIndex,
                                        })
                                    }
                                />
                            );
                        })
                    ) : (
                        <p className="pci-module-step__empty-roofs">
                            Waiting for live roof modules from the 3D viewport…
                        </p>
                    )}
                </div>

                {hoverModuleCell && hoverLayout ? (
                    <div className="pci-module-tooltip" role="status">
                        <strong>{hoverModuleCell.id}</strong>
                        <span>
                            {isPreContractShadePending(hoverModuleCell.solarAccess) ? (
                                'Loading shade'
                            ) : (
                                <>
                                    SA{' '}
                                    <em
                                        style={{
                                            color: getPreContractModuleShadeColor(hoverModuleCell),
                                        }}
                                    >
                                        {hoverModuleCell.solarAccess.toFixed(1)}%
                                    </em>
                                </>
                            )}
                        </span>
                        <span>
                            {hoverModuleCell.included
                                ? 'click to remove'
                                : cellBlocked(hoverModuleCell)
                                  ? isPreContractPurplePanel(hoverModuleCell.solarAccess)
                                      ? 'purple panel excluded'
                                      : 'grey panel excluded'
                                  : 'click to include'}
                        </span>
                    </div>
                ) : null}

                {moduleSaLegend}
                    </>
                ) : (
                    <>
                        <div className="pci-tree-cutting-bar" aria-label="Tree cutting">
                            <p className="pci-tree-cutting-bar__hint">
                                Click a tree to ghost it in 3D
                            </p>
                            <div className="pci-sa-legend pci-sa-legend--inline" aria-hidden>
                                <span className="pci-sa-legend__item">
                                    <i className="pci-tree-swatch pci-tree-swatch--live" />
                                    Standing
                                </span>
                                <span className="pci-sa-legend__item">
                                    <i className="pci-tree-swatch pci-tree-swatch--ghost" />
                                    Ghosted
                                </span>
                            </div>
                        </div>
                        <SiteTreeBoard trees={form.siteTrees} onToggle={toggleTreeGhost} />
                    </>
                )}
            </section>

            {!embedded ? (
            <div className="pci-board-fab-dock" aria-label="Board actions">
                <div className="pci-board-fab-dock__start">
                    <button
                        type="button"
                        className="pci-board-fab__btn"
                        onClick={onBack}
                        title="Back"
                        aria-label="Back"
                    >
                        <CaretLeft size={20} weight="bold" />
                    </button>
                </div>
                <div className="pci-board-fab-dock__main" aria-hidden />
                <div className="pci-board-fab-dock__end">
                    <button
                        type="button"
                        className="pci-board-fab__btn"
                        onClick={onOpenPricing}
                        title="Pricing"
                        aria-label="Pricing"
                    >
                        <CurrencyDollar size={20} weight="bold" />
                    </button>
                    <div className="pci-board-fab__ai" role="group" aria-label="AI designs">
                        {hasPreviousAiDesigns ? (
                            <>
                                <button
                                    type="button"
                                    className="pci-board-fab__ai-btn"
                                    disabled={runNewDisabled}
                                    title={runAiTitle}
                                    onClick={() => onRunAiDesign(false)}
                                >
                                    Run AI Designs
                                </button>
                                <button
                                    type="button"
                                    className="pci-board-fab__ai-btn pci-board-fab__ai-btn--primary"
                                    disabled={viewPreviousDisabled}
                                    title="View previous AI designs"
                                    onClick={() => onRunAiDesign(true)}
                                >
                                    <Sparkle size={16} weight="fill" />
                                    View
                                </button>
                            </>
                        ) : (
                            <button
                                type="button"
                                className="pci-board-fab__ai-btn pci-board-fab__ai-btn--primary pci-board-fab__ai-btn--solo"
                                disabled={runNewDisabled}
                                title={runAiTitle}
                                onClick={() => onRunAiDesign(false)}
                            >
                                <Sparkle size={16} weight="fill" />
                                {modulesLoading ? 'Waiting…' : 'Run AI Designs'}
                            </button>
                        )}
                    </div>
                </div>
            </div>
            ) : null}
        </div>
    );
};

function layoutIsActive(layout: {
    modules: Array<{ included: boolean } | null>;
}): boolean {
    return layout.modules.some((cell) => Boolean(cell?.included));
}

function selectionsFromLayouts(
    layouts: Array<{ modules: Array<{ included: boolean } | null> }>,
): boolean[] {
    return layouts.map((layout) => layoutIsActive(layout));
}

function formatKwh(value: number): string {
    return `${Math.round(value).toLocaleString()} kWh`;
}

const MaxfillTargetCard: React.FC<MaxfillTargetCardProps> = ({
    productionKwh,
    targetKwh,
    moduleCount,
    waitingForShade,
    usedLiveProduction,
}) => {
    const hasProduction = productionKwh != null && productionKwh > 0;
    const hasTarget = targetKwh != null && targetKwh > 0;
    const offsetPercent =
        hasProduction && hasTarget ? Math.round((productionKwh / targetKwh) * 100) : null;
    const gapKwh =
        hasProduction && hasTarget ? Math.round(productionKwh - targetKwh) : null;
    const barMax =
        hasProduction && hasTarget ? Math.max(productionKwh, targetKwh) : 0;
    const barPercent =
        hasProduction && hasTarget
            ? Math.max(4, Math.min(100, (productionKwh / barMax) * 100))
            : 0;
    const markPercent =
        hasProduction && hasTarget ? Math.min(100, (targetKwh / barMax) * 100) : 100;
    const closeness =
        waitingForShade
            ? 'Waiting for oneDRAW shade to calculate max-fill production.'
            : gapKwh == null
              ? hasTarget
                  ? 'Max-fill production is not available yet.'
                  : 'Add annual usage to see how close max-fill is to target.'
              : gapKwh === 0
                ? 'On target'
                : gapKwh > 0
                  ? `${formatKwh(gapKwh)} over target`
                  : `${formatKwh(Math.abs(gapKwh))} short of target`;
    const closenessTone =
        gapKwh == null ? '' : gapKwh >= 0 ? ' pci-maxfill-target__closeness--over' : ' pci-maxfill-target__closeness--short';
    const sourceLabel = waitingForShade
        ? 'Waiting for oneDRAW shade…'
        : usedLiveProduction
          ? 'From oneDRAW max-fill on active roofs'
          : 'Estimated from active-roof max-fill';

    return (
        <section className="pci-module-step__card pci-maxfill-target" aria-label="oneDRAW max-fill vs target">
            <p className="pci-board-v2-label">oneDRAW max-fill</p>
            <h2 className="pci-module-step__title">Production vs target</h2>
            <p className="pci-maxfill-target__lead">{sourceLabel}</p>
            <dl className="pci-maxfill-target__stats">
                <div>
                    <dt>Max-fill</dt>
                    <dd>{hasProduction ? formatKwh(productionKwh) : '—'}</dd>
                </div>
                <div>
                    <dt>Target</dt>
                    <dd>{hasTarget ? formatKwh(targetKwh) : '—'}</dd>
                </div>
                <div>
                    <dt>Offset</dt>
                    <dd>{offsetPercent != null ? `${offsetPercent}%` : '—'}</dd>
                </div>
                <div>
                    <dt>Modules</dt>
                    <dd>{moduleCount > 0 ? moduleCount : '—'}</dd>
                </div>
            </dl>
            {hasTarget && hasProduction ? (
                <div className="pci-maxfill-target__bar" aria-hidden>
                    <span className="pci-maxfill-target__bar-fill" style={{ width: `${barPercent}%` }} />
                    <span
                        className="pci-maxfill-target__bar-mark"
                        style={{ left: `${markPercent}%` }}
                        title="Target"
                    />
                </div>
            ) : null}
            <p className={`pci-maxfill-target__closeness${closenessTone}`}>{closeness}</p>
        </section>
    );
};

function RoofModuleBoard({
    layout,
    multiplierDetail,
    inactive,
    includeGreyPanels,
    includePurplePanels,
    onToggleRoof,
    onToggle,
    onHover,
}: RoofModuleBoardProps) {
    const presentModules = layout.modules.filter((m): m is NonNullable<typeof m> => Boolean(m));
    const activeCount = presentModules.filter((m) => m.included).length;
    const isBlocked = (solarAccess: number) =>
        !isPreContractPanelAllowed(solarAccess, includeGreyPanels, includePurplePanels);
    const selectableCount = presentModules.filter((m) => !isBlocked(m.solarAccess)).length;
    const roofToggleDisabled = inactive && selectableCount === 0;
    const roofMultiplier = multiplierDetail.multiplier;

    return (
        <div
            className={`pci-roof-modules pci-roof-modules--${layout.moduleOrientation ?? 'portrait'}${
                inactive ? ' pci-roof-modules--unused' : ''
            }`}
            style={{ ['--pci-roof-cols' as string]: String(layout.cols) }}
        >
            <header className="pci-roof-modules__head">
                <div className="pci-roof-modules__title">
                    <strong>
                        {layout.label}
                        {inactive ? ' (inactive)' : ''}
                    </strong>
                    <span>
                        {activeCount}/{presentModules.length} active
                    </span>
                    <span
                        className="pci-roof-modules__mult"
                        title={
                            multiplierDetail.meanSolarAccess != null
                                ? `Avg SA ${multiplierDetail.meanSolarAccess.toFixed(1)}% across ${multiplierDetail.includedCount} included module${multiplierDetail.includedCount === 1 ? '' : 's'}`
                                : 'Multiplier for included modules on this roof'
                        }
                    >
                        Mult {roofMultiplier != null ? roofMultiplier.toFixed(3) : '—'}
                        {multiplierDetail.meanSolarAccess != null ? (
                            <em className="pci-roof-modules__mult-sa">
                                {multiplierDetail.meanSolarAccess.toFixed(1)}% SA
                            </em>
                        ) : null}
                    </span>
                </div>
                <button
                    type="button"
                    className={`pci-roof-modules__active-btn${
                        inactive
                            ? ' pci-roof-modules__active-btn--off'
                            : ' pci-roof-modules__active-btn--on'
                    }`}
                    aria-pressed={!inactive}
                    disabled={roofToggleDisabled}
                    title={
                        roofToggleDisabled
                            ? 'Every panel on this roof is excluded by shaded-panel settings'
                            : inactive
                              ? 'Select all modules on this roof'
                              : 'Deselect all modules on this roof'
                    }
                    onClick={onToggleRoof}
                >
                    {inactive ? 'Inactive' : 'Active'}
                </button>
            </header>
            <div className="pci-roof-modules__scroller">
                <div className="pci-roof-modules__grid">
                    {layout.modules.map((cell, index) => {
                    if (!cell) {
                        return <span key={`gap-${layout.roofIndex}-${index}`} className="pci-roof-module pci-roof-module--gap" />;
                    }
                    const shadePending = isPreContractShadePending(cell.solarAccess);
                    const color = getPreContractModuleShadeColor(cell);
                    const moduleNum = cell.id.replace(/^R\d+M/, '');
                    const blocked = isBlocked(cell.solarAccess);
                    return (
                        <button
                            key={cell.id}
                            type="button"
                            className={`pci-roof-module${
                                shadePending ? ' pci-roof-module--loading' : ''
                            }${cell.included ? '' : ' pci-roof-module--hatched'}${
                                blocked ? ' pci-roof-module--blocked' : ''
                            }`}
                            style={
                                shadePending
                                    ? undefined
                                    : {
                                          backgroundColor: color,
                                          ['--pci-sa' as string]: color,
                                      }
                            }
                            title={
                                shadePending
                                    ? `${cell.id} · Loading`
                                    : blocked
                                      ? `${cell.id} · ${cell.solarAccess}% SA · ${
                                            isPreContractPurplePanel(cell.solarAccess)
                                                ? 'purple panel excluded'
                                                : 'grey panel excluded'
                                        }`
                                      : `${cell.id} · ${cell.solarAccess}% SA${
                                            cell.multiplier != null &&
                                            Number.isFinite(cell.multiplier) &&
                                            cell.multiplier > 0
                                                ? ` · Mult ${cell.multiplier.toFixed(3)}`
                                                : ''
                                        }`
                            }
                            aria-disabled={blocked}
                            onClick={() => onToggle(index)}
                            onMouseEnter={() => onHover(index)}
                        >
                            <span>M{moduleNum}</span>
                        </button>
                    );
                })}
                </div>
            </div>
        </div>
    );
}

function SiteTreeBoard({ trees, onToggle }: SiteTreeBoardProps) {
    const [showAll, setShowAll] = useState(false);

    const visibleTrees = useMemo(() => {
        const ranked = [...trees].sort((a, b) => {
            const am = Number(a.multiplier);
            const bm = Number(b.multiplier);
            const aImpact = Number.isFinite(am) ? am : 0;
            const bImpact = Number.isFinite(bm) ? bm : 0;
            return bImpact - aImpact;
        });
        if (showAll) return ranked;
        return ranked.filter((tree) => {
            const multiplier = Number(tree.multiplier);
            return Number.isFinite(multiplier) && multiplier > 0;
        });
    }, [trees, showAll]);

    const standingCount = visibleTrees.filter((tree) => !tree.ghosted).length;
    const hiddenCount = Math.max(0, trees.length - visibleTrees.length);

    return (
        <>
            {trees.length === 0 ? (
                <p className="pci-module-step__empty-roofs">
                    Waiting for trees from the 3D viewport…
                </p>
            ) : visibleTrees.length === 0 ? (
                <p className="pci-module-step__empty-roofs">
                    No impact trees yet.{' '}
                    <button
                        type="button"
                        className="pci-tree-board__link"
                        onClick={() => setShowAll(true)}
                    >
                        Show all {trees.length}
                    </button>
                </p>
            ) : (
                <div className="pci-roof-modules pci-tree-board">
                    <header className="pci-roof-modules__head">
                        <div className="pci-roof-modules__title">
                            <strong>{showAll ? 'All trees' : 'Impact trees'}</strong>
                            <span>
                                {standingCount}/{visibleTrees.length} standing
                                {hiddenCount > 0 ? ` · ${hiddenCount} hidden` : ''}
                            </span>
                        </div>
                        <button
                            type="button"
                            className="pci-tree-board__link"
                            onClick={() => setShowAll((on) => !on)}
                        >
                            {showAll ? 'Impact only' : 'Show all'}
                        </button>
                    </header>
                    <div className="pci-tree-board__grid">
                        {visibleTrees.map((tree) => (
                            <SiteTreeIcon key={tree.id} tree={tree} onToggle={onToggle} />
                        ))}
                    </div>
                </div>
            )}
        </>
    );
}

function SiteTreeIcon({ tree, onToggle }: SiteTreeIconProps) {
    const handleClick = () => onToggle(tree.id);
    const multiplierLabel =
        typeof tree.multiplier === 'number' && Number.isFinite(tree.multiplier)
            ? tree.multiplier.toFixed(3)
            : '—';
    const name = tree.label || tree.id;
    return (
        <button
            type="button"
            className={`pci-site-tree${tree.ghosted ? ' pci-site-tree--ghosted' : ''}`}
            title={
                tree.ghosted
                    ? `${name} · ${multiplierLabel} · ghosted — click to restore`
                    : `${name} · ${multiplierLabel} · standing — click to ghost`
            }
            aria-pressed={tree.ghosted}
            onClick={handleClick}
        >
            <span className="pci-site-tree__id">{name}</span>
            <span className="pci-site-tree__multi">{multiplierLabel}</span>
        </button>
    );
}

export default PreContractModuleStep;
